import { memo, useState, useCallback, useRef, useEffect } from 'react'
import { Stack, Title, Paper, Group, Button, Stepper, Anchor } from 'components'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { IconChevronLeft, IconCheck, IconX } from '@tabler/icons-react'
import { notifications } from 'context'
import { StructureStep, StructureStepRef } from '../components/StructureStep'
import { StandardsAndSkillsStep } from '../components/StandardsAndSkillsStep'
import { EditProjectSkeleton } from '../components/EditProjectSkeleton'
import { ArcStepperProvider } from '../context/ArcStepperContext'
import { useProjectForm } from '../hooks/useProjectForm'
import { projectService } from '../services/ProjectService'

enum ManualStep { DETAILS = 0, SKILLS = 1 }

const HASH_MAP: Record<string, ManualStep> = { 'details': 0, 'skills': 1 }
const STEP_HASH: Record<ManualStep, string> = { 0: 'details', 1: 'skills' }

interface ManualFlowViewProps {
  isEditMode: boolean
  projectId?: string
  onBack?: () => void
}

export const ManualFlowView = memo<ManualFlowViewProps>(({ isEditMode, projectId: initialProjectId, onBack }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formData, loadData } = useProjectForm()
  const structureStepRef = useRef<StructureStepRef>(null)

  const getInitialStep = (): ManualStep => {
    const hash = window.location.hash.slice(1)
    const requestedStep = HASH_MAP[hash] ?? ManualStep.DETAILS
    if (!isEditMode && !initialProjectId && requestedStep === ManualStep.SKILLS) {
      return ManualStep.DETAILS
    }
    return requestedStep
  }

  const [currentStep, setCurrentStep] = useState<ManualStep>(getInitialStep)
  const [projectId, setProjectId] = useState<string | null>(initialProjectId || null)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(isEditMode)

  useEffect(() => {
    const hash = `#${STEP_HASH[currentStep]}`
    if (window.location.hash !== hash) {
      window.history.replaceState(null, '', hash)
    }
  }, [currentStep])

  useEffect(() => {
    if (!isEditMode || !initialProjectId) {
      setIsLoading(false)
      return
    }
    const load = async () => {
      try {
        const data = await projectService.getProjectForEdit(initialProjectId)
        if (data) loadData(data)
      } catch (error) {
        const isProjectFinished = error instanceof Error && error.message === 'PROJECT_FINISHED'
        const message = isProjectFinished
          ? t('arc_error_project_finished')
          : t('arc_error_loading_project')
        notifications.show({ message, color: 'red', icon: <IconX size={18} /> })
        navigate(isProjectFinished ? `/projects/${initialProjectId}` : '/projects')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [isEditMode, initialProjectId, loadData, t, navigate])

  const handleNextStep = useCallback(async () => {
    const isValid = structureStepRef.current?.validateAndSave()
    if (!isValid) return

    setIsSaving(true)
    try {
      if (isEditMode && projectId) {
        await projectService.updateProjectDetails(projectId, formData)
      } else if (!projectId) {
        const result = await projectService.createDraftProject(formData)
        if (result.success && result.data.projectId) {
          setProjectId(result.data.projectId)
        } else {
          throw new Error(result.error?.message || t('arc_project_created_error'))
        }
      }
      setCurrentStep(ManualStep.SKILLS)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('arc_project_created_error')
      notifications.show({ message, color: 'red', icon: <IconX size={18} /> })
    } finally {
      setIsSaving(false)
    }
  }, [formData, projectId, isEditMode, t])

  const handlePreviousStep = useCallback(() => setCurrentStep(ManualStep.DETAILS), [])

  const handleFinish = useCallback(async () => {
    if (!projectId) return
    setIsSaving(true)
    try {
      if (isEditMode) {
        await projectService.updateProjectDetails(projectId, formData)
      }
      if (formData.skillsAndStandards.length > 0) {
        await projectService.updateProjectSkills(projectId, formData.skillsAndStandards)
      }
      notifications.show({
        message: isEditMode ? t('arc_project_saved_success') : t('arc_project_created_success'),
        color: 'green',
        icon: <IconCheck size={18} />
      })
      navigate(`/projects/${projectId}`)
    } catch (error) {
      notifications.show({
        message: isEditMode ? t('arc_error_updating_project') : t('arc_project_created_error'),
        color: 'red',
        icon: <IconX size={18} />
      })
    } finally {
      setIsSaving(false)
    }
  }, [projectId, formData, isEditMode, navigate, t])

  const handleStepClick = useCallback((step: number) => {
    if (step === ManualStep.SKILLS && !projectId && !isEditMode) return
    setCurrentStep(step as ManualStep)
  }, [projectId, isEditMode])

  if (isLoading) {
    return <EditProjectSkeleton />
  }

  return (
    <ArcStepperProvider initialProjectId={projectId || undefined} isEditMode={isEditMode}>
      <Stack spacing="lg">
        <Stack spacing={0} align="flex-start">
          {isEditMode ? (
            <Anchor
              component={Link}
              to="/projects"
              sx={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <IconChevronLeft size={16} />
              {t('arc_back_to_projects')}
            </Anchor>
          ) : (
            <Anchor onClick={onBack} sx={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <IconChevronLeft size={16} />
              {t('arc_button_back')}
            </Anchor>
          )}
          <Title order={1}>{isEditMode ? t('arc_edit_project_title') : t('new_project_title')}</Title>
        </Stack>

        <Stepper
          active={currentStep}
          onStepClick={handleStepClick}
          color="turquoise"
          breakpoint="sm"
          styles={{
            steps: { justifyContent: 'flex-start', gap: 16 },
            separator: { maxWidth: 100 },
          }}
        >
          <Stepper.Step
            label={t('arc_manual_step_details')}
            allowStepSelect={currentStep > ManualStep.DETAILS || isEditMode}
          >
            <Paper shadow="sm" p="lg" radius="md" withBorder mt="md">
              <StructureStep ref={structureStepRef} />
              <Group position="right" mt="xl">
                <Button size="lg" color="turquoise" loading={isSaving} onClick={handleNextStep}>
                  {t('arc_button_next')}
                </Button>
              </Group>
            </Paper>
          </Stepper.Step>

          <Stepper.Step
            label={t('arc_manual_step_skills')}
            allowStepSelect={!!projectId || isEditMode}
          >
            <Paper shadow="sm" p="lg" radius="md" withBorder mt="md">
              <StandardsAndSkillsStep />
              <Group position="apart" mt="xl">
                <Button size="lg" variant="default" leftIcon={<IconChevronLeft size={18} />} onClick={handlePreviousStep}>
                  {t('arc_button_back')}
                </Button>
                <Button size="lg" color="turquoise" loading={isSaving} onClick={handleFinish}>
                  {isEditMode ? t('arc_button_save_project') : t('arc_button_create_project')}
                </Button>
              </Group>
            </Paper>
          </Stepper.Step>
        </Stepper>
      </Stack>
    </ArcStepperProvider>
  )
})

ManualFlowView.displayName = 'ManualFlowView'
